import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mockDramas } from '../../../mocks/dramas';
import { createDramaUrl } from '../../../hooks/useNavigation';

const PopularDramasCarousel = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const popularDramas = [...mockDramas]
    .sort((a, b) => {
      const parseViews = (v: string) => {
        const num = parseFloat(v);
        if (v.includes('M')) return num * 1000000;
        if (v.includes('K')) return num * 1000;
        return num;
      };
      return parseViews(b.views) - parseViews(a.views);
    })
    .map((drama, index) => ({
      id: drama.id,
      slug: drama.slug,
      genreSlug: drama.genreSlug,
      title: drama.title,
      poster: drama.poster,
      banner: drama.banner,
      rating: drama.rating,
      episodes: drama.episodes,
      genre: drama.genre[0],
      rank: index + 1,
      newEpisode: drama.status === 'ongoing',
      views: drama.views,
      country: drama.country,
      description: drama.description,
    }));

  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
      setTimeout(checkScrollButtons, 300);
    }
  };

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      if (scrollRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 10;
        if (isAtEnd) {
          scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          scrollRef.current.scrollBy({ left: 220, behavior: 'smooth' });
        }
        setTimeout(checkScrollButtons, 300);
      }
    }, 3500);
    return () => clearInterval(interval);
  }, [isPaused]);

  const getRankStyle = (rank: number) => {
    if (rank === 1) return 'from-amber-400 to-amber-600 text-white shadow-amber-400/40';
    if (rank === 2) return 'from-gray-300 to-gray-5 00 text-white shadow-gray-400/30';
    if (rank === 3) return 'from-orange-600 to-orange-800 text-white shadow-orange-600/30';
    return 'from-gray-500 to-gray-600 text-white shadow-gray-500/20';
  };

  return (
    <section>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-rose-500 to-red-500 rounded-xl shadow-lg shadow-rose-500/20 text-white">
            <i className="ri-fire-fill text-lg"></i>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Popular Dramas This Week</h2>
            <p className="text-xs text-gray-500">Trending shows everyone is watching</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scroll('left')}
            disabled={!canScrollLeft}
            className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
              canScrollLeft
                ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm'
                : 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-arrow-left-s-line text-sm"></i>
          </button>
          <button
            onClick={() => scroll('right')}
            disabled={!canScrollRight}
            className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-all cursor-pointer whitespace-nowrap ${
              canScrollRight
                ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 shadow-sm'
                : 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
            }`}
          >
            <i className="ri-arrow-right-s-line text-sm"></i>
          </button>
          <Link
            to="/dramas"
            className="ml-1 text-sm font-medium text-rose-5 00 hover:text-rose-600 flex items-center gap-1 whitespace-nowrap cursor-pointer"
          >
            View All
            <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
      </div>

      {/* Carousel */}
      <div
        ref={scrollRef}
        onScroll={checkScrollButtons}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        className="flex gap-5 overflow-x-auto pb-2 -mx-1 px-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {popularDramas.map((drama) => (
          <Link
            key={drama.id}
            to={createDramaUrl(drama.genreSlug, drama.slug)}
            className="flex-shrink-0 w-[280px] group cursor-pointer"
            onMouseEnter={() => setHoveredId(drama.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            {/* Horizontal card */}
            <div className="relative rounded-2xl overflow-hidden bg-white shadow-sm border border-gray-100 hover:shadow-xl hover:border-gray-200 transition-all duration-300 hover:-translate-y-1 flex h-[140px]">
              {/* Left poster */}
              <div className="relative w-[100px] h-[140px] flex-shrink-0 overflow-hidden">
                <img
                  src={drama.poster}
                  alt={drama.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                {/* Rank badge */}
                <div className={`absolute top-2 left-2 w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br ${getRankStyle(
                  drama.rank
                )} text-xs font-extrabold shadow-lg`}>
                  {drama.rank}
                </div>

                {/* Hover play */}
                <div
                  className={`absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity duration-300 ${
                    hoveredId === drama.id ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <div className="w-9 h-9 flex items-center justify-center bg-white/90 rounded-full shadow-xl">
                    <i className="ri-play-fill text-rose-500 text-base ml-0.5"></i>
                  </div>
                </div>
              </div>

              {/* Right content */}
              <div className="flex-1 p-3.5 flex flex-col justify-between min-w-0">
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="px-2 py-0.5 bg-rose-50 text-rose-600 text-[10px] font-semibold rounded-md">
                      {drama.genre}
                    </span>
                    {drama.newEpisode && (
                      <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold rounded-md animate-pulse">
                        NEW EP
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 group-hover:text-rose-500 transition-colors truncate">
                    {drama.title}
                  </h3>
                  <p className="text-[11px] text-gray-500 line-clamp-2 mt-1 leading-relaxed">
                    {drama.description}
                  </p>
                </div>

                {/* Bottom stats */}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-50">
                  <div className="flex items-center gap-3 text-[10px] text-gray-400">
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-amber-400 text-[10px]"></i>
                      <span className="font-semibold text-gray-600">{drama.rating}</span>
                    </span>
                    <span>{drama.episodes} eps</span>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-gray-400">
                    <i className="ri-eye-line text-[10px]"></i>
                    {drama.views}
                  </div>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default PopularDramasCarousel;
